import prefix from '../../Helpers/Prefix';
import Colorable from '../../Mixins/Colorable';
import MergeClasses from '../../Mixins/MergeClasses';
import each from '../../Helpers/Functions/each';
import isArray from '../../Helpers/Functions/isArray';
import isEmpty from '../../Helpers/Functions/isEmpty';
import isObject from '../../Helpers/Functions/isObject';
import kebabCase from '../../Helpers/Functions/kebabCase';

const EMPTY_CLASS = 'is-empty';
const FOCUS_CLASS = 'has-focus';
const CHANGED_CLASS = 'has-changed';
const CUSTOM_PREFIX = 'custom';

function addClass(el, vnode, css) {
    // el.classList.add(css);
    vnode.context.$el.classList.add(css);
}

function removeClass(el, vnode, css) {
    // el.classList.remove(css);
    vnode.context.$el.classList.remove(css);
}

function addEmptyClass(el, vnode) {
    if(isEmpty(el.value) || (el.tagName === 'SELECT' && el.selectedIndex === -1)) {
        addClass(el, vnode, EMPTY_CLASS);
    }
}

export default {

    inheritAttrs: false,

    mixins: [
        Colorable,
        MergeClasses
    ],

    props: {

        /**
         * Show type activity indicator.
         *
         * @property Boolean
         */
        activity: {
            type: Boolean,
            default: false
        },

        /**
         * Is the form control a custom styled component.
         *
         * @property Boolean
         */
        custom: {
            type: Boolean,
            default: false
        },

        /**
         * The value of label element. If no value, no label will appear.
         *
         * @property String
         */
        label: [Number, String],

        /**
         * The field id attribute value.
         *
         * @property String
         */
        value: {
            default: null
        },

        /**
         * Add form-group wrapper to input
         *
         * @property String
         */
        group: {
            type: Boolean,
            default: true
        },

        /**
         * An inline field validation error.
         *
         * @property String|Boolean
         */
        error: [Boolean, String],

        /**
         * An inline field validation errors passed as object with key/value
         * pairs. If errors passed as an object, the form name will be used for
         * the key.
         *
         * @property Object|Boolean
         */
        errors: {
            type: Object,
            default() {
                return {};
            }
        },

        /**
         * Some feedback to add to the field once the field is successfully
         * valid.
         *
         * @property String
         */
        feedback: [String, Array],

        /**
         * An array of event names that correlate with callback functions
         *
         * @property Function
         */
        bindEvents: {
            type: Array,
            default() {
                return ['focus', 'blur', 'change', 'click', 'keypress', 'keyup', 'keydown', 'progress', 'paste'];
            }
        },

        /**
         * The default class name assigned to the control element
         *
         * @property String
         */
        defaultControlClass: {
            type: String,
            default: 'form-control'
        },

        /**
         * The default label class assigned to the label element
         *
         * @property String
         */
        labelClass: [Object, String],

        /**
         * Hide the label for browsers, but leave it for screen readers.
         *
         * @property String
         */
        hideLabel: Boolean,

        /**
         * The invalid property
         *
         * @property String
         */
        invalid: Boolean,

        /**
         * The valid property
         *
         * @property String
         */
        valid: Boolean,

        /**
         * Additional margin/padding classes for fine control of spacing
         *
         * @property String
         */
        spacing: String,

        /**
         * The size of the form control
         *
         * @property String
         */
        size: {
            type: String,
            default: 'md',
            validate: value => ['sm', 'md', 'lg'].indexOf(value) !== -1
        },

        /**
         * Display the form field inline
         *
         * @property String
         */
        inline: Boolean,

        /**
         * Some instructions to appear under the field label
         *
         * @property String
         */
        helpText: [Number, String]

    },

    directives: {
        bindEvents: {
            bind(el, binding, vnode) {
                function changedValue(el, value) {
                    addClass(el, vnode, CHANGED_CLASS);

                    if(!isEmpty(value) || (el.selectedIndex && el.selectedIndex > -1)) {
                        removeClass(el, vnode, EMPTY_CLASS);
                    }
                    else if(!el.classList.contains(CHANGED_CLASS)) {
                        addClass(el, vnode, EMPTY_CLASS);
                    }

                    if(el.tagName === 'SELECT' && el.querySelector('[value=""]')) {
                        el.querySelector('[value=""]').selected = !value;
                    }
                }

                vnode.context.$watch('value', (value) => {
                    changedValue(vnode.context.$el, value);
                });

                el.addEventListener('blur', event => {
                    if(el.classList.contains(EMPTY_CLASS)) {
                        removeClass(el, vnode, CHANGED_CLASS);
                    }

                    removeClass(el, vnode, FOCUS_CLASS);
                });

                /*                
                el.addEventListener('input', event => {
                    changedValue(event.target, event.target.value);
                });
                */
                
                el.addEventListener('change', event => {
                    changedValue(event.target, event.target.value);
                });

                // Add/remove the has-focus class from the form control
                el.addEventListener('focus', event => {
                    addClass(el, vnode, FOCUS_CLASS);
                });

                // Bubble the native events up to the vue component.
                each(vnode.context.bindEvents, name => {
                    el.addEventListener(name, event => {
                        vnode.context.$emit(name, event);
                    });
                });

                if(el.selectedIndex >= 0) {
                    el.setAttribute('data-selected-index', el.selectedIndex);
                }
            },
            inserted(el, binding, vnode) {
                addEmptyClass(el, vnode);

                if(typeof el.selectedIndex === 'number' && el.selectedIndex > -1) {
                    // addClass(el, vnode, CHANGED_CLASS);
                }
            },
            update(el, binding, vnode) {
                addEmptyClass(el, vnode);
            }
        }
    },

    methods: {

        blur() {
            if(this.getInputField()) {
                this.getInputField().blur();
            }
        },

        focus() {
            if(this.getInputField()) {
                this.getInputField().focus();
            }
        },

        getInputField() {
            return this.$el.querySelector(
                '.form-control, input, select, textarea'
            );
        },

        getFieldErrors() {
            let errors = this.error || this.errors;

            if(isObject(this.errors)) {
                errors = this.errors[this.$attrs.name || this.$attrs.id];
            }

            return !errors || isArray(errors) || isObject(errors) ? errors : [errors];
        }

    },

    computed: {

        controlAttributes() {
            return Object.keys(this.$attrs)
                .concat([['class', this.controlClasses]])
                .reduce((carry, key) => {
                    if(isArray(key)) {
                        carry[key[0]] = key[1];
                    }
                    else {
                        carry[key] = this[key] || this.$attrs[key];
                    }

                    return carry;
                }, {});
        },

        controlClass() {
            return this.custom ? this.customControlClass : (
                this.defaultControlClass + (this.plaintext ? '-plaintext' : '')
            );
        },

        controlSizeClass() {
            return prefix(this.size, this.controlClass);
        },

        customControlClass() {
            return 'custom-control';
        },

        formGroupClasses() {
            const name = prefix(kebabCase(this.$options.name), this.custom ? CUSTOM_PREFIX : '');

            return this.mergeClasses(name, prefix(this.size, name), {
                'has-activity': this.activity,
                'is-valid': !!(this.valid || this.validFeedback),
                'is-invalid': !!(this.invalid || this.invalidFeedback)
            });
        },

        controlClasses() {
            return this.mergeClasses(
                this.controlClass,
                this.colorableClasses,
                this.controlSizeClass,
                (this.spacing || ''),
                ((this.valid || this.validFeedback) ? 'is-valid' : ''),
                ((this.invalid || this.invalidFeedback) ? 'is-invalid' : '')
            );
        },

        hasDefaultSlot() {
            return !!this.$slots.default;
        },

        invalidFeedback() {
            if(this.error) {
                return this.error;
            }

            const errors = this.getFieldErrors();

            return isArray(errors) ? errors.filter(error => {
                return error && typeof error === 'string';
            }).join('<br>') : errors;
        },

        validFeedback() {
            return isArray(this.feedback) ? this.feedback.join('<br>') : this.feedback;
        }

    }

};
